import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { authApi } from '@services/api';
import type { User, LoginCredentials, RegisterData, AuthTokens } from '@types';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Load user on mount
  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        const userData = await authApi.getCurrentUser();
        setUser(userData);
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      // Clear tokens if getCurrentUser fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null); // Ensure user state is null
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('[Auth] 1. Calling login API...');
      const tokens = await authApi.login(credentials);
      console.log('[Auth] 2. Tokens received:', {
        has_access: !!tokens.access_token,
        has_refresh: !!tokens.refresh_token,
      });

      saveTokens(tokens);
      console.log('[Auth] 3. Tokens saved to localStorage');

      console.log('[Auth] 4. Loading user data...');
      await loadUser();
      console.log('[Auth] 5. User loaded successfully');

      toast.success('Login successful!');
    } catch (error) {
      console.error('[Auth] Login error:', error);
      toast.error('Login failed. Please check your credentials.');
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      const tokens = await authApi.register(data);
      saveTokens(tokens);
      await loadUser();

      // If /auth/me fails after registration, loadUser() clears tokens.
      // Re-save tokens and set a minimal user so isAuthenticated = true
      // for the redirect to /onboarding.
      if (!localStorage.getItem('access_token')) {
        saveTokens(tokens);
        setUser({ email: data.email, company_name: data.company_name } as User);
        setIsLoading(false);
      }

      toast.success('Registration successful!');
    } catch (error) {
      toast.error('Registration failed. Please try again.');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
      clearTokens();
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      clearTokens();
      setUser(null);
    }
  };

  const refreshUser = async () => {
    await loadUser();
  };

  const saveTokens = (tokens: AuthTokens) => {
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
  };

  const clearTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
